import { UserProfile, UserRole, AccountTier, normalizeAccountTier } from '../types/user';

export const DEFAULT_TESTER_ALLOWED_ROLES: UserRole[] = [
  UserRole.BASIC_USER,
  UserRole.CONSULTING_ASTROLOGER,
  UserRole.TESTER,
];

/**
 * Validates if the authenticated identity has designated Tester status.
 * Evaluates true authenticated identity (baseRole/isTester), NOT the active simulation role.
 */
export function isTester(user: UserProfile | null): boolean {
  if (!user) return false;
  const cleanEmail = (user.email || '').toLowerCase().trim();
  return (
    user.baseRole === UserRole.TESTER ||
    user.isTester === true ||
    cleanEmail.startsWith('tester@') ||
    cleanEmail === 'tester' ||
    cleanEmail.includes('+tester')
  );
}

/**
 * Security guard: Determines if the user is authorized to view and use role-switching.
 * STRICTLY restricted to verified Tester accounts.
 * Normal Users, Astrologers, and Admins return false.
 */
export function canSwitchRole(user: UserProfile | null): boolean {
  return isTester(user);
}

/**
 * Returns the permitted testing roles for the designated Tester.
 * Non-testers always receive an empty list.
 */
export function getAllowedTestRoles(user: UserProfile | null): UserRole[] {
  if (!user || !isTester(user)) return [];
  if (Array.isArray(user.allowedTestRoles) && user.allowedTestRoles.length > 0) {
    return user.allowedTestRoles;
  }
  return DEFAULT_TESTER_ALLOWED_ROLES;
}

/**
 * Authorizes a role transition. Checks:
 * 1. User is an authorized Tester.
 * 2. Target role is explicitly in allowedTestRoles.
 * 3. Target role is a valid UserRole.
 */
export function canActivateRole(user: UserProfile | null, targetRole: UserRole): boolean {
  if (!user || !canSwitchRole(user)) return false;
  const allowed = getAllowedTestRoles(user);
  return allowed.includes(targetRole);
}

/**
 * Switches the activeRole for a Tester while strictly preserving the underlying baseRole.
 * Throws an error or safely falls back if authorization fails.
 */
export function switchActiveRole(user: UserProfile, targetRole: UserRole): UserProfile {
  if (!canActivateRole(user, targetRole)) {
    console.warn(`[RBAC] Unauthorized role switch attempt to "${targetRole}" by user "${user.email}".`);
    return user;
  }

  const baseRole = user.baseRole || UserRole.TESTER;
  const isTestMode = targetRole !== baseRole;

  return {
    ...user,
    baseRole,
    activeRole: targetRole,
    role: targetRole, // Set primary role to activeRole so app views respond seamlessly
    allowedTestRoles: getAllowedTestRoles(user),
    isTester: true,
    testMode: isTestMode,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Resets the activeRole back to the primary Tester role.
 */
export function resetToTesterRole(user: UserProfile): UserProfile {
  if (!isTester(user)) return user;
  const baseRole = user.baseRole || UserRole.TESTER;
  return {
    ...user,
    baseRole,
    activeRole: baseRole,
    role: baseRole,
    allowedTestRoles: getAllowedTestRoles(user),
    isTester: true,
    testMode: false,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Sanitizes and validates a persisted session on application startup.
 * Ensures activeRole is authorized; otherwise falls back safely to baseRole.
 */
export function sanitizeSessionUser(user: UserProfile | null): UserProfile | null {
  if (!user) return null;

  const tester = isTester(user);
  if (tester) {
    const baseRole = user.baseRole || UserRole.TESTER;
    const allowed = getAllowedTestRoles(user);
    const active = user.activeRole || user.role || baseRole;

    const validActive = allowed.includes(active) ? active : baseRole;

    return {
      ...user,
      baseRole,
      activeRole: validActive,
      role: validActive,
      allowedTestRoles: allowed,
      isTester: true,
      testMode: validActive !== baseRole,
    };
  } else {
    // Normal user / Astrologer / Admin: baseRole equals role; cannot have testMode or allowedTestRoles
    const baseRole = user.baseRole || user.role || UserRole.BASIC_USER;
    return {
      ...user,
      baseRole,
      activeRole: baseRole,
      role: baseRole,
      allowedTestRoles: [],
      isTester: false,
      testMode: false,
    };
  }
}

/**
 * User-friendly display names for roles in English and Hindi.
 */
export function getRoleLabel(role: UserRole | string, lang: string = 'en'): string {
  const isHi = lang === 'hi';
  switch (role) {
    case UserRole.TESTER:
      return isHi ? 'परीक्षक (Tester)' : 'Tester';
    case UserRole.SUPER_ADMIN:
      return isHi ? 'सुपर एडमिन (Super Admin)' : 'Super Admin';
    case UserRole.ADMIN:
      return isHi ? 'एडमिन (Admin)' : 'Admin';
    case UserRole.CONSULTING_ASTROLOGER:
      return isHi ? 'परामर्श ज्योतिषी (Astrologer)' : 'Consulting Astrologer';
    case UserRole.PRO_ASTROLOGER:
      return isHi ? 'प्रो ज्योतिषी (Pro Astrologer)' : 'Pro Astrologer';
    case UserRole.NOVICE_ASTROLOGER:
      return isHi ? 'प्रारंभिक ज्योतिषी (Novice)' : 'Novice Astrologer';
    case UserRole.ADVANCED_ASTROLOGY_USER:
      return isHi ? 'उन्नत ज्योतिष सदस्य (Advanced User)' : 'Advanced Jyotish User';
    case UserRole.BASIC_USER:
      return isHi ? 'उपयोगकर्ता (User)' : 'User';
    case UserRole.GUEST:
      return isHi ? 'अतिथि (Guest)' : 'Guest';
    default:
      return isHi ? 'उपयोगकर्ता (User)' : 'User';
  }
}

/**
 * Validates if the authenticated identity has Super Admin privileges.
 * Strictly restricted to Super Admin role and verified administrator identities.
 */
export function isSuperAdminUser(user: UserProfile | null): boolean {
  if (!user) return false;
  const effectiveRole = user.role || user.activeRole || user.baseRole;
  const email = (user.email || '').toLowerCase().trim();
  return (
    effectiveRole === UserRole.SUPER_ADMIN ||
    (effectiveRole as string) === 'super_admin' ||
    (effectiveRole as string) === 'SUPER_ADMIN' ||
    email === 'bijen.rohilla@gmail.com' ||
    email.startsWith('superadmin@') ||
    email.startsWith('super_admin@')
  );
}

/**
 * Validates if the user is strictly restricted to the Basic profile tier.
 * Returns true for Guests, unauthenticated users, BASIC_USER role, and FREE/BASIC account tiers.
 * Returns false for Administrators, Consulting Astrologers, Pro Astrologers, and Testers (unless actively simulating BASIC_USER).
 */
export function isBasicTierUser(user: UserProfile | null): boolean {
  if (!user) return true;

  const effectiveRole = user.role || user.activeRole || user.baseRole;
  if (effectiveRole === UserRole.SUPER_ADMIN || effectiveRole === UserRole.ADMIN) return false;
  if (effectiveRole === UserRole.CONSULTING_ASTROLOGER || effectiveRole === UserRole.PRO_ASTROLOGER) return false;
  if (effectiveRole === UserRole.ADVANCED_ASTROLOGY_USER) return false;
  if (effectiveRole === UserRole.TESTER && !user.testMode) return false;

  // Elevate if account tier is Pro Astro or Acharya (including normalized legacy strings)
  const { canonicalTier } = normalizeAccountTier(user.tier);
  if (canonicalTier === AccountTier.PRO_ASTRO || canonicalTier === AccountTier.ACHARYA) {
    return false;
  }

  if (
    effectiveRole === UserRole.BASIC_USER ||
    effectiveRole === UserRole.GUEST ||
    (effectiveRole as string) === 'basic_user' ||
    (effectiveRole as string) === 'guest'
  ) {
    return true;
  }

  if (
    canonicalTier === AccountTier.GUEST ||
    canonicalTier === AccountTier.BASIC
  ) {
    return true;
  }

  return false;
}

